import React, { useMemo } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  createCustomCellularIcon,
  createCustomEthernetIcon,
  createCustomNoneIcon,
  createCustomWifiIcon,
} from 'clipsal-cortex-icons/src/custom-dynamic-icons';

import { SEHomeCard } from '../../../../common/components/SEHomeCard';
import { selectCellularModems, selectInverters } from '../../../site/siteSlice';
import { AccountMenuButton } from '../../AccountMenuButton';
import { getStrengthValue } from '../helpers';
import { HardwareConnection } from '../types';

export function ConnectionsList({ connections }: { connections: HardwareConnection[] }) {
  const { t } = useTranslation();

  return (
    <Flex direction={'column'} w="100%" data-testid="device-connection" mt={8}>
      <Text fontWeight={'semibold'} mb={2}>
        {t('Hardware.connections')}
      </Text>
      <SEHomeCard borderTopLeftRadius={'5px'} p={0}>
        {connections.map((connection, index) => (
          <DetailsConnectionGroupItem
            key={connection.type}
            connection={connection}
            index={index}
            length={connections.length}
          />
        ))}
      </SEHomeCard>
    </Flex>
  );
}

export function DetailsConnectionGroupItem({
  index,
  connection,
  length,
}: {
  index: number;
  connection: HardwareConnection;
  length: number;
}) {
  const navigate = useNavigate();
  const modems = useSelector(selectCellularModems);
  const inverters = useSelector(selectInverters);
  const { t } = useTranslation();

  const { label, Icon, link } = useMemo(() => {
    const { connection_status: connectionStatus, signal_strength: signalStrength, type, enabled } = connection;
    const internalLabel = t(`Hardware.connectionKey.${type ?? 'DISABLED'}`);
    const signalKey = connectionStatus === 'Not connected' ? connectionStatus : signalStrength ?? connectionStatus;
    const strengthValue = getStrengthValue(enabled ? signalKey : undefined);
    const color = '#3DCD58';

    if (type === 'WIFI') {
      return {
        label: internalLabel,
        Icon: createCustomWifiIcon(strengthValue, color),
        link: '../wifi?direction=forward',
      };
    }
    if (type === 'CELLULAR_MODEM') {
      return {
        label: internalLabel,
        Icon: createCustomCellularIcon(strengthValue, color),
        link: modems[0]?.id ? `../../${modems[0]?.id}/view?backURL=../../${inverters[0]?.id}/view` : '../view',
      };
    }
    if (type === 'ETHERNET') {
      return {
        label: internalLabel,
        Icon: createCustomEthernetIcon(strengthValue, color),
        link: '../ethernet?direction=forward',
      };
    }
    return {
      label: internalLabel,
      Icon: createCustomNoneIcon(),
      link: '../view',
    };
  }, [connection, inverters, modems, t]);

  return (
    <AccountMenuButton
      borderBottomRightRadius={index === length - 1 ? undefined : 0}
      includeBottomDivider={index !== length - 1}
      onClick={() => navigate(link)}
      data-testid="device-connection-item"
    >
      <Flex align={'center'} overflow="auto">
        <Icon mr={2} />
        <Text fontSize={14} fontWeight="normal">
          {label}
        </Text>
      </Flex>
    </AccountMenuButton>
  );
}
